'use client';

import { c as _c } from "react/compiler-runtime";
import { useLocale, useTranslations } from 'next-intl';
import type { MouseEvent } from 'react';
import { ThesisVotePill } from '@/components/social/debate/thesis-vote-pill';
import type { ThesisItem } from '@/components/social/types';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Icon } from '@/components/ui/icon';
import { Comment03IconSolid, LinkForwardIconSolid } from '@/components/ui/icon/glyphs';
import { useAuthGate } from '@/hooks/use-auth-gate';
import { formatCount } from '@/lib/format/format-count';
import { withDemoMode } from '@/lib/network';
import { useUserNetwork } from '@/lib/network/client';
import { buildThesisShareUrl, openShareModal } from '@/lib/share';
import { cn } from '@/lib/ui/utils';

// ---------------------------------------------------------------------------
// Constants — sizes/colors lifted from spec 094 Figma (node 3434:1549)
// ---------------------------------------------------------------------------

const REPLY_ICON_SIZE_PX = 'size-[18px]' as const; // ARBITRARY: 18px reply icon per Figma 3434:1557
const SHARE_ICON_SIZE_PX = 'size-[18px]' as const; // ARBITRARY: 18px share icon per Figma 3434:1561
const ACTIONS_ROW_GAP = 'gap-6' as const; // 24px between the upvote, reply, share groups
const COUNT_SLOT_WIDTH = 'w-7' as const; // 28px count slot (closest Tailwind step to Figma's 26px)

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ThesisActionsProps {
  thesis: ThesisItem;
  replyCount: number;
  isAuthenticated: boolean;
  /**
   * - `interactive` (default): reply count is a tappable AnimatedButton. Tap
   *   always calls `onToggleReplies`; reading reply threads is public.
   * - `display`: reply count renders as a non-interactive span. Used by the
   *   portfolio variant of `ThesisCard` where the whole card is a pressable
   *   Link; a tap on the reply count falls through to that parent Link.
   */
  mode?: 'interactive' | 'display';
  onToggleReplies?: () => void;
  className?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ThesisActions(t0) {
  const $ = _c(31);
  const {
    thesis,
    replyCount,
    isAuthenticated,
    mode: t1,
    onToggleReplies,
    className
  } = t0;
  const mode = t1 === undefined ? "interactive" : t1;
  const t = useTranslations("social.debate");
  const tShared = useTranslations("social");
  const tShare = useTranslations("thesis.share");
  const locale = useLocale();
  const {
    openLogin
  } = useAuthGate();
  const {
    network
  } = useUserNetwork();
  let t2;
  if ($[0] !== openLogin) {
    t2 = function handleAuthRequired() {
      openLogin();
    };
    $[0] = openLogin;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const handleAuthRequired = t2;
  let t3;
  if ($[2] !== onToggleReplies) {
    t3 = function handleReplyClick(e) {
      e.stopPropagation();
      e.preventDefault();
      onToggleReplies?.();
    };
    $[2] = onToggleReplies;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const handleReplyClick = t3;
  let t4;
  if ($[4] !== network || $[5] !== tShare || $[6] !== thesis) {
    t4 = function handleShareClick(e_0) {
      e_0.stopPropagation();
      e_0.preventDefault();
      if (typeof window === "undefined") {
        return;
      }
      const u = new URL(buildThesisShareUrl(thesis.signature), window.location.origin);
      withDemoMode(u, network === "mainnet" ? "mainnet" : "devnet");
      openShareModal({
        surface: {
          kind: "thesis",
          signature: thesis.signature
        },
        url: u.toString(),
        text: tShare("tweetText"),
        title: tShare("modalTitle")
      });
    };
    $[4] = network;
    $[5] = tShare;
    $[6] = thesis;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const handleShareClick = t4;
  let t5;
  if ($[8] !== className) {
    t5 = cn("flex items-center text-foreground-tertiary", ACTIONS_ROW_GAP, className);
    $[8] = className;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== handleAuthRequired || $[11] !== isAuthenticated || $[12] !== thesis) {
    t6 = <ThesisVotePill thesis={thesis} isAuthenticated={isAuthenticated} onAuthRequired={handleAuthRequired} size="md" className="relative z-10" />;
    $[10] = handleAuthRequired;
    $[11] = isAuthenticated;
    $[12] = thesis;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== handleReplyClick || $[15] !== locale || $[16] !== mode || $[17] !== replyCount || $[18] !== t) {
    t7 = mode === "interactive" ? <AnimatedButton variant="tertiary" size="sm" onClick={handleReplyClick} aria-label={t("aria_replies", {
      count: replyCount
    })} className="relative z-10 h-6 cursor-pointer gap-0.5 bg-transparent p-0 text-foreground-tertiary hover:bg-transparent"><span className="inline-flex items-center gap-0.5"><Icon icon={Comment03IconSolid} className={cn(REPLY_ICON_SIZE_PX, "shrink-0 text-foreground-tertiary")} /><span className={cn(COUNT_SLOT_WIDTH, "shrink-0 text-center text-body font-medium tabular-nums text-foreground-tertiary")}>{formatCount(replyCount, locale)}</span></span></AnimatedButton> : <span data-testid="thesis-actions-reply-count" className="inline-flex h-6 items-center gap-0.5 text-foreground-tertiary"><Icon icon={Comment03IconSolid} className={cn(REPLY_ICON_SIZE_PX, "shrink-0 text-foreground-tertiary")} /><span className={cn(COUNT_SLOT_WIDTH, "shrink-0 text-center text-body font-medium tabular-nums text-foreground-tertiary")}>{formatCount(replyCount, locale)}</span></span>;
    $[14] = handleReplyClick;
    $[15] = locale;
    $[16] = mode;
    $[17] = replyCount;
    $[18] = t;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== tShared) {
    t8 = tShared("share_thesis");
    $[20] = tShared;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Icon icon={LinkForwardIconSolid} className={cn(SHARE_ICON_SIZE_PX, "shrink-0 text-foreground-tertiary")} />;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== handleShareClick || $[24] !== t8) {
    t10 = <AnimatedButton variant="tertiary" size="sm" onClick={handleShareClick} aria-label={t8} className="relative z-10 h-6 cursor-pointer bg-transparent p-0 text-foreground-tertiary hover:bg-transparent">{t9}</AnimatedButton>;
    $[23] = handleShareClick;
    $[24] = t8;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== t10 || $[27] !== t5 || $[28] !== t6 || $[29] !== t7) {
    t11 = <div className={t5}>{t6}{t7}{t10}</div>;
    $[26] = t10;
    $[27] = t5;
    $[28] = t6;
    $[29] = t7;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  return t11;
}